// T4 team composition rules — CLIENT mirror of the backend engine
// (backend helpers/oireachtasTeamRules.js). Presentation-level: drives the
// live per-card validation status; the server re-validates at quote/checkout
// (acceptance C1), so the two files MUST stay in lockstep — same rule codes,
// same skip-when-data-absent behavior, same gender tokens as cascade.js.
//
// Everything reads DATA (competition teamConfig/bornYear, event.teamRules,
// the céilí dance reference) — never per-event logic (entry-cascade.md).

import { birthYearOf, normalizeGender } from "./cascade";

export const TEAM_TYPES = new Set([
  "team_2h", "team_3h", "team_4h", "team_6h", "team_8h",
  "figure", "dance_drama", "adult_team",
]);

// ECRO syllabus rule 14 defaults; any event.teamRules field overrides.
export const ECRO_TEAM_RULES = {
  ageModel: "per_dancer",
  properAgeMinPercent: 50,
  lowerAgeMaxPercent: 50,
  repeatFromLowerMaxPercent: 25,
  mixed4hMinMales: 1,
  mixed4hMaxMales: 2,
  mixed8hMinMales: 2,
  mixed8hMaxMales: 4,
};

export const isTeamComp = (comp) => !!comp && TEAM_TYPES.has(comp.type);

// ---- display order for the team competition picker -------------------------
// The catalog's array order can NOT carry this: competitions come back in
// insertion order and the template clone interleaves the groups (staging
// returns 2-Hand -> 6-Hand -> 8-Hand -> Dance Drama -> 3-Hand -> 4-Hand ...),
// so the picker read as random. Rebuild the order the syllabus lists teams in:
// by hand count, then youngest band first with Adult last, then Girls before
// Mixed. Derived from `type`/`bornYear`/`gender`, so it holds for any syllabus.
const TEAM_TYPE_RANK = {
  team_2h: 0, team_3h: 1, team_4h: 2, team_6h: 3, team_8h: 4,
  adult_team: 5, figure: 6, dance_drama: 7,
};
const GENDER_RANK = { girls: 0, ladies: 0, boys: 1, men: 1, mixed: 2 };

// Age is compared on the born-year BAND, not the label: the youngest band has
// the highest `from`, and the open-ended senior bands (no `from`) come last,
// oldest last. `code` breaks what's left — "18 & Over" and "Adult" carry an
// identical band, and T8-99-M sorts before T8-AC.
const bornBand = (comp) => {
  const b = comp && comp.bornYear;
  if (Array.isArray(b) && b.length) {
    const ys = b.map(Number).filter((n) => !Number.isNaN(n));
    return ys.length ? { from: Math.min(...ys), to: Math.max(...ys) } : { from: -Infinity, to: Infinity };
  }
  if (b && typeof b === "object") {
    const n = (v, f) => (v == null || Number.isNaN(Number(v)) ? f : Number(v));
    return { from: n(b.from, -Infinity), to: n(b.to, Infinity) };
  }
  const one = Number(b);
  return b == null || Number.isNaN(one) ? { from: -Infinity, to: Infinity } : { from: one, to: one };
};

export const compareTeamComps = (a, b) => {
  const rank = (c) => (TEAM_TYPE_RANK[c.type] == null ? 99 : TEAM_TYPE_RANK[c.type]);
  if (rank(a) !== rank(b)) return rank(a) - rank(b);
  const ba = bornBand(a);
  const bb = bornBand(b);
  if (ba.from !== bb.from) return bb.from - ba.from; // youngest band first
  if (ba.to !== bb.to) return bb.to - ba.to;
  const g = (c) => { const r = GENDER_RANK[String(c.gender || "").toLowerCase()]; return r == null ? 3 : r; };
  if (g(a) !== g(b)) return g(a) - g(b);
  return String(a.code || "").localeCompare(String(b.code || ""));
};

// ---- Add-dancer picker gate ------------------------------------------------
// ONLY the two rules a regional director never grants an exception to (Devin,
// 2026-08-13): a male dancer in a girls-only competition, and a dancer older
// than the band (dancing down — syllabus 14c "may not dance down an age
// group"). Both are HARD (non-overridable) errors in validateTeams here and on
// the server, so a cart saved before this gate shipped can't be attested past
// it either — the gate is a convenience, the rule engine is the enforcement.
//
// Everything else validateTeams flags stays SELECTABLE on purpose. Dancing two
// age groups up (TEAM_AGE_SKIP) and repeating more than once in a hand
// (TEAM_REPEAT_HAND) ARE reviewable by the regional director under 14e, so the
// teacher has to be able to pick the dancer and record the override — gating
// them here would make that impossible. Do not "tidy" this by gating on
// everything the validator can flag.
//
// Missing gender / birthday never excludes: same skip-when-data-absent
// behavior as validateTeams. Open-ended senior bands (15 & Over, Adult) have
// no lower bound, so nobody is "too old" for them and the age gate correctly
// does nothing.
export const dancerAllowedInComp = (dancer, comp) => {
  if (!dancer || !comp) return true;
  if (["girls", "ladies"].includes(comp.gender) && normalizeGender(dancer.gender) === "male") return false;
  const band = bandOf(comp.bornYear);
  const year = birthYearOf(dancer.birthday);
  if (!band || year == null) return true;
  if (Number.isFinite(band.from) && year < band.from) return false;
  // Adult = a separate track, so being YOUNGER than the band is a category
  // error here, not the usual legal dance-up (see isAdultComp).
  if (isAdultComp(comp) && Number.isFinite(band.to) && year > band.to) return false;
  return true;
};

export function handOf(comp) {
  if (!comp) return null;
  const size = comp.teamConfig && comp.teamConfig.size;
  if (size) return Number(size);
  const m = String(comp.type || "").match(/^team_(\d)h$/);
  return m ? Number(m[1]) : null;
}

// The separate ADULT track (syllabus rule 8 — 18+ register as SOLO or ADULT),
// as opposed to the top rungs of the youth ladder ("15 & Over", "18 & Over")
// which share its born-year band but ARE climbed one step at a time.
export const isAdultComp = (comp) =>
  String((comp && comp.ageGroup) || "").trim().toLowerCase() === "adult";

export function bandOf(bornYear) {
  if (bornYear == null) return null;
  if (Array.isArray(bornYear)) {
    const years = bornYear.map(Number).filter((y) => !Number.isNaN(y));
    if (!years.length) return null;
    return { from: Math.min(...years), to: Math.max(...years) };
  }
  if (typeof bornYear === "object") {
    return {
      from: bornYear.from != null ? Number(bornYear.from) : -Infinity,
      to: bornYear.to != null ? Number(bornYear.to) : Infinity,
    };
  }
  const y = Number(bornYear);
  return Number.isNaN(y) ? null : { from: y, to: y };
}

// normalizeGender is imported from ./cascade — single client-side source of
// truth for the gender token list (matches the server's normalizeGender).

const dancerName = (d) =>
  d ? [d.fname, d.lname].filter(Boolean).join(" ") || String(d._id) : "A dancer";

function effectiveRules(teamRules) {
  const rules = { ...ECRO_TEAM_RULES };
  for (const [k, v] of Object.entries(teamRules || {})) {
    if (v != null && v !== "") rules[k] = v;
  }
  return rules;
}

const keyOf = (team, index) => String((team && team.key) || `team-${index}`);

/** Mirror of the server's validateTeams — see the backend file for rule docs. */
export function validateTeams({
  teams = [],
  competitionsById = {},
  dancersById = {},
  ceiliDancesByKey = {},
  savedTeams = [],
  teamRules = {},
} = {}) {
  const rules = effectiveRules(teamRules);
  const errors = [];

  // duplicate keys break team identity (cross-team checks, override coverage,
  // conversion) — hard error, mirrors the server engine. The UI's newTeamKey()
  // never produces these; this guards rehydrated/foreign carts.
  const keyCounts = new Map();
  teams.forEach((t, i) => {
    const k = keyOf(t, i);
    keyCounts.set(k, (keyCounts.get(k) || 0) + 1);
  });
  for (const [k, n] of keyCounts) {
    if (n > 1) {
      errors.push({
        teamKey: k,
        code: "TEAM_KEY_DUP",
        message: "Two teams share the same identity — remove one and re-add it.",
        overridable: false,
      });
    }
  }

  const bandsByHand = new Map();
  for (const comp of Object.values(competitionsById)) {
    if (!isTeamComp(comp)) continue;
    const hand = handOf(comp);
    const band = bandOf(comp.bornYear);
    if (!hand || !band || !Number.isFinite(band.from)) continue;
    const list = bandsByHand.get(hand) || [];
    if (!list.some((b) => b.from === band.from && b.to === band.to)) list.push(band);
    bandsByHand.set(hand, list);
  }
  for (const list of bandsByHand.values()) list.sort((a, b) => b.from - a.from);

  const rosterOf = (t) =>
    [...new Set((t.members || []).map((m) => String(m && m.dancer_id ? m.dancer_id : m)))];
  const allTeams = [
    ...teams.map((t, i) => ({ key: keyOf(t, i), competition_id: t.competition_id, roster: rosterOf(t) })),
    ...savedTeams.map((t) => ({ key: `saved:${t._id}`, competition_id: t.competition_id, roster: rosterOf(t) })),
  ];

  teams.forEach((team, index) => {
    const teamKey = keyOf(team, index);
    const err = (code, message, overridable = false) =>
      errors.push({ teamKey, code, message, overridable });

    if (!team.competition_id) {
      err("TEAM_COMP_REQUIRED", "Choose a team competition.");
      return;
    }
    const comp = competitionsById[String(team.competition_id)];
    if (!comp || !isTeamComp(comp)) {
      err("TEAM_COMP_INVALID", "This is not a team competition of this event.");
      return;
    }
    const hand = handOf(comp);
    const size = (comp.teamConfig && comp.teamConfig.size) || null;

    const memberIds = (team.members || []).map((m) =>
      String(m && m.dancer_id ? m.dancer_id : m)
    );
    const seen = new Set();
    for (const id of memberIds) {
      if (seen.has(id)) {
        err("TEAM_MEMBER_DUP", `${dancerName(dancersById[id])} appears twice on this team.`);
      }
      seen.add(id);
    }
    let complete;
    if (size) {
      complete = memberIds.length === size;
      if (!complete) {
        err("TEAM_SIZE", `This competition needs exactly ${size} dancers (currently ${memberIds.length}).`);
      }
    } else {
      complete = memberIds.length >= 1;
      if (!complete) err("TEAM_SIZE", "Add at least one dancer to this team.");
    }

    const danceOptions = (comp.teamConfig && comp.teamConfig.danceOptions) || [];
    let dance = null;
    if (danceOptions.length) {
      if (!team.danceKey) {
        err("TEAM_DANCE_REQUIRED", "Choose the team's dance.");
      } else if (!danceOptions.includes(team.danceKey)) {
        err("TEAM_DANCE_INVALID", `${team.danceKey} is not an allowed dance for ${comp.code || "this competition"}.`);
      } else {
        dance = ceiliDancesByKey[team.danceKey] || null;
        if (!dance) err("TEAM_DANCE_INVALID", `Unknown céilí dance: ${team.danceKey}.`);
      }
    }
    if (dance) {
      const figures = dance.figures || [];
      if (figures.length) {
        if (!team.figure) {
          err("TEAM_FIGURE_REQUIRED", `${dance.name} requires a figure of choice.`);
        } else if (!figures.includes(team.figure)) {
          err("TEAM_FIGURE_INVALID", `${team.figure} is not a figure of ${dance.name}.`);
        }
      } else if (team.figure) {
        err("TEAM_FIGURE_INVALID", `${dance.name} has no figure of choice.`);
      }
    }

    const members = memberIds.map((id) => dancersById[id]).filter(Boolean);
    const band = bandOf(comp.bornYear);
    const ageChecks = rules.ageModel === "per_dancer" && band != null;

    if (ageChecks) {
      for (const d of members) {
        const y = birthYearOf(d.birthday);
        if (y == null) continue;
        // NOT overridable — a minor on an adult team is not something a
        // regional director grants. Mirrors the server.
        if (isAdultComp(comp) && Number.isFinite(band.to) && y > band.to) {
          err("TEAM_AGE_ADULT_ONLY", `${dancerName(d)} is under 18 — ${comp.code || "this competition"} is an adult competition.`);
          continue;
        }
        // NOT overridable either — the picker gates these dancers out, so this
        // only fires on a cart saved before the gate. Mirrors the server.
        if (y < band.from) {
          err("TEAM_AGE_DOWN", `${dancerName(d)} is older than this competition's age group (no dancing down).`);
          continue;
        }
        const bands = bandsByHand.get(hand) || [];
        const ownIdx = bands.findIndex((b) => y >= b.from && y <= b.to);
        const compIdx = bands.findIndex((b) => b.from === band.from && b.to === band.to);
        if (ownIdx >= 0 && compIdx >= 0 && compIdx > ownIdx + 1) {
          err("TEAM_AGE_SKIP", `${dancerName(d)} may dance their own or the next-higher age group only.`, true);
        }
      }
    }

    if (complete && members.length) {
      if (ageChecks) {
        const knowns = members
          .map((d) => ({ d, y: birthYearOf(d.birthday) }))
          .filter((x) => x.y != null);
        if (knowns.length) {
          const proper = knowns.filter((x) => x.y >= band.from && x.y <= band.to).length;
          const lower = knowns.filter((x) => x.y > band.to).length;
          const pct = (n) => (n / knowns.length) * 100;
          if (rules.properAgeMinPercent != null && pct(proper) < rules.properAgeMinPercent) {
            err("TEAM_AGE_MIN_PROPER", `At least ${rules.properAgeMinPercent}% of the team must be from the ${comp.ageGroup || "proper"} age group.`, true);
          }
          if (rules.lowerAgeMaxPercent != null && pct(lower) > rules.lowerAgeMaxPercent) {
            err("TEAM_AGE_LOWER_MAX", `At most ${rules.lowerAgeMaxPercent}% of the team may come from a lower age group.`, true);
          }
        }
      }

      const genders = members.map((d) => normalizeGender(d.gender)).filter(Boolean);
      const males = genders.filter((g) => g === "male").length;
      // Girls/ladies-only + a male member: NOT overridable (picker gate).
      // The mixed male COUNTS below stay overridable. Mirrors the server.
      if (["girls", "ladies"].includes(comp.gender) && males > 0) {
        err("TEAM_GENDER_MIX", `${comp.code || "This competition"} is ${comp.gender}-only.`);
      }
      if (comp.gender === "mixed" && genders.length === members.length) {
        const min = rules[`mixed${hand}hMinMales`];
        const max = rules[`mixed${hand}hMaxMales`];
        if (min != null && males < min) {
          err("TEAM_GENDER_MIX", `A mixed ${hand}-hand team needs at least ${min} male dancer${min > 1 ? "s" : ""}.`, true);
        } else if (max != null && males > max) {
          err("TEAM_GENDER_MIX", `A mixed ${hand}-hand team allows at most ${max} male dancers.`, true);
        }
      }
    }

    // A school may enter ANY number of teams in the same competition (Devin
    // 2026-08-20) — but a DANCER may sit on only ONE of them. Hard gate; the
    // member picker also filters these dancers out (Teams.jsx). An identical
    // (competition, member set) saved team is the SAME team being resubmitted
    // and doesn't count against its own dancers. Mirrors the server.
    {
      const roster = rosterOf(team);
      const rosterSet = new Set(roster);
      const sameCompOthers = allTeams.filter(
        (t) =>
          t.key !== teamKey &&
          String(t.competition_id) === String(team.competition_id) &&
          !(
            t.key.startsWith("saved:") &&
            t.roster.length === roster.length &&
            t.roster.every((id) => rosterSet.has(id))
          )
      );
      const dupIds = [
        ...new Set(sameCompOthers.flatMap((t) => t.roster.filter((id) => rosterSet.has(id)))),
      ];
      for (const id of dupIds) {
        const d = dancersById[id];
        err(
          "TEAM_DANCER_DUP_COMP",
          `${d ? dancerName(d) : "A dancer"} is already on another ${comp.code || "this competition"} team — a dancer may only be on one team per competition.`
        );
      }
    }

    if (hand) {
      const compOf = (t) => competitionsById[String(t.competition_id)] || null;
      const othersSameHand = allTeams.filter(
        (t) => t.key !== teamKey && handOf(compOf(t)) === hand
      );
      let lowerRepeats = 0;
      for (const id of seen) {
        const elsewhere = othersSameHand.filter((t) => t.roster.includes(id));
        if (elsewhere.length + 1 > 2) {
          err(
            "TEAM_REPEAT_HAND",
            `${dancerName(dancersById[id])} is already in ${elsewhere.length} other ${hand}-hand team${elsewhere.length > 1 ? "s" : ""} (one repeat per hand).`,
            true
          );
        }
        if (band && Number.isFinite(band.from)) {
          const fromLower = elsewhere.some((t) => {
            const b = bandOf((compOf(t) || {}).bornYear);
            return b && Number.isFinite(b.from) && b.from > band.from;
          });
          if (fromLower) lowerRepeats += 1;
        }
      }
      if (
        complete &&
        memberIds.length &&
        rules.repeatFromLowerMaxPercent != null &&
        (lowerRepeats / memberIds.length) * 100 > rules.repeatFromLowerMaxPercent
      ) {
        err("TEAM_REPEAT_LOWER", `At most ${rules.repeatFromLowerMaxPercent}% of the team may repeat from a lower-age ${hand}-hand team.`, true);
      }
    }
  });

  return { ok: errors.length === 0, errors };
}

/** Errors that block progressing: hard errors + violations with no covering override. */
export function blockingTeamErrors(teams = [], errors = []) {
  const overrideByKey = new Map(teams.map((t, i) => [keyOf(t, i), t.override || {}]));
  return errors.filter((e) => {
    if (!e.overridable) return true;
    const o = overrideByKey.get(String(e.teamKey)) || {};
    return !(o.overridden && (o.overriddenRules || []).includes(e.code));
  });
}

/** "already in 1 four-hand" — the pick-list / member-chip hint (mockup). */
export function usedHint(dancerId, currentTeam, teams, savedTeams, competitionsById) {
  const comp = competitionsById[String(currentTeam.competition_id || "")];
  const hand = handOf(comp);
  if (!hand) return null;
  const id = String(dancerId);
  const currentKey = String(currentTeam.key || "");
  let count = 0;
  const check = (t, key) => {
    if (key === currentKey) return;
    const c = competitionsById[String(t.competition_id || "")];
    if (handOf(c) !== hand) return;
    const ids = (t.members || []).map((m) => String(m && m.dancer_id ? m.dancer_id : m));
    if (ids.includes(id)) count += 1;
  };
  teams.forEach((t) => check(t, String(t.key || "")));
  (savedTeams || []).forEach((t) => check(t, `saved:${t._id}`));
  if (!count) return null;
  const word = { 2: "two-hand", 3: "three-hand", 4: "four-hand", 6: "six-hand", 8: "eight-hand" }[hand] || `${hand}-hand`;
  return `already in ${count} ${word}`;
}
